import { Flex, IconButton } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@sphere/icons';
import { rem } from 'polished';
import { PaginationButton } from './PaginationButton';
import { DOTS, PaginationDots } from './PaginationDots';
import { usePagination } from './usePagination';

export interface PaginationProps {
  current: number;
  totalPages: number;
  isLoading: boolean;
  onNavigate: (page: number) => void;
  withControls?: boolean;
}

export const Pagination = ({
  current,
  totalPages,
  isLoading,
  onNavigate,
  withControls = true,
}: PaginationProps) => {
  const paginationRange = usePagination({
    totalPageCount: totalPages,
    currentPage: current,
  });

  return (
    <Flex gap="space.8" align="flex-end" justify="center">
      {withControls && (
        <IconButton
          variant="clear"
          boxSize={{ base: rem(28), md: rem(38) }}
          minW="unset"
          aria-label="Previous"
          onClick={() => onNavigate(current - 1)}
          icon={<ChevronLeftIcon boxSize={{ base: rem(12), md: rem(16) }} />}
          transition="opacity 160ms ease-in-out"
          _hover={{ opacity: 0.4 }}
          _disabled={{ opacity: 0.4, color: 'gray.5', cursor: 'not-allowed' }}
          isDisabled={current === 1}
        />
      )}
      {paginationRange?.map((page, index) =>
        page === DOTS || typeof page === 'string' ? (
          <PaginationDots key={page + index} />
        ) : (
          <PaginationButton
            key={page}
            page={page}
            currentPage={current}
            onClick={onNavigate}
            isLoading={isLoading}
          />
        ),
      )}
      {withControls && (
        <IconButton
          variant="clear"
          boxSize={{ base: rem(28), md: rem(38) }}
          minW="unset"
          aria-label="Next"
          onClick={() => onNavigate(current + 1)}
          icon={<ChevronRightIcon boxSize={{ base: rem(12), md: rem(16) }} />}
          transition="opacity 160ms ease-in-out"
          _hover={{ opacity: 0.4 }}
          _disabled={{ opacity: 0.4, color: 'gray.5', cursor: 'not-allowed' }}
          isDisabled={current === totalPages}
        />
      )}
    </Flex>
  );
};
